/**
 * Public, crawlable pages and their images. One list feeds the web app's content pages,
 * the build-time pre-renderer (full HTML per page) and the server's sitemap, so they can't drift.
 */

export interface SeoImage {
  /** Encoded widths, smallest first; the last one is also the og:image and sitemap image. */
  variants: Array<{ width: number; url: string }>;
  width: number;
  height: number;
  /** What the picture shows, for screen readers and image search. */
  alt: string;
  /** Visible caption under the picture, and the sitemap caption. */
  caption: string;
}

const shot = (name: string, height: number, alt: string, caption: string): SeoImage => ({
  variants: [
    { width: 640, url: `/images/${name}-640.webp` },
    { width: 1200, url: `/images/${name}-1200.webp` },
  ],
  width: 1200,
  height,
  alt,
  caption,
});

export const SEO_IMAGES = {
  hero: {
    variants: [
      { width: 600, url: '/snake-feast-multiplayer-snake-arena-600.webp' },
      { width: 1200, url: '/snake-feast-multiplayer-snake-arena-1200.webp' },
    ],
    width: 1200,
    height: 630,
    alt: 'Snake Feast multiplayer snake arena with power-ups and a closing battle royale zone',
    caption: 'A Snake Feast arena: up to eight snakes, food, power-ups and a closing zone.',
  },
  bigArena: shot(
    'big-arena-multiplayer-snake-game',
    675,
    'A green snake and a long purple rival snake crossing the big Snake Feast arena, surrounded by fruit, cakes and other food',
    'The big arena: a huge live map with up to 24 snakes, a minimap and instant respawns.',
  ),
  royaleZone: shot(
    'battle-royale-snake-closing-zone',
    675,
    'Battle royale snake match: the red danger zone closes in from the left while a "Zone closing in 4s" warning shows above a green snake',
    'Battle royale: the red zone eats the edge of the arena one ring at a time.',
  ),
  royaleMobile: shot(
    'battle-royale-snake-mobile-close-call',
    555,
    'Snake Feast on a phone in landscape: a green snake slips past a blue snake for a "Close call! +2" bonus as the battle royale zone closes',
    'Turning away from a rival at the last moment earns a close-call bonus.',
  ),
  classic: shot(
    'classic-multiplayer-snake-match',
    675,
    'Classic multiplayer snake match with a green snake and an orange-red rival snake hunting for food on a dark grid',
    'Classic rules, real opponents: eat, grow and make your rivals crash first.',
  ),
  kingOfTheHill: shot(
    'king-of-the-hill-snake-mode',
    675,
    'King of the Hill snake mode: a green snake and a crowned red snake fight for the gold-bordered hill while a purple snake approaches',
    'King of the Hill: score while your head is on the hill, and keep everyone else off it.',
  ),
  magnet: shot(
    'snake-magnet-power-up',
    555,
    'A magnet power-up glowing on the board next to a green snake, with cookies, fruit and fries around it',
    'Power-ups like the magnet appear on the board and last eight seconds.',
  ),
  swipeControls: shot(
    'snake-game-swipe-controls-mobile',
    555,
    'Snake Feast on a phone at the start of a match, with the spawn shield on and the hint "Swipe anywhere to turn"',
    'On a phone, swipe anywhere to turn, or switch to the joystick or D-pad.',
  ),
} satisfies Record<string, SeoImage>;

export type SeoImageId = keyof typeof SEO_IMAGES;

export interface SeoPage {
  /** Clean URL, e.g. "/how-to-play". The build writes it as "how-to-play.html". */
  path: string;
  /** Short name for breadcrumbs and footers. */
  name: string;
  title: string;
  description: string;
  /** Images on the page, the first one used for link previews. */
  images: SeoImageId[];
  priority: string;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  noindex?: boolean;
  /** Guides: an article (author, dates in schema.org) listed on the guides hub with this summary. */
  guide?: { published: string; updated?: string; summary: string };
}

const PUBLISHED = '2026-10-30';
const guide = (path: string, name: string, title: string, description: string, images: SeoImageId[], summary: string): SeoPage => ({
  path,
  name,
  title,
  description,
  images,
  priority: '0.7',
  changefreq: 'monthly',
  guide: { published: PUBLISHED, summary },
});

/** The guides, in the order the hub lists them. */
export const GUIDE_PAGES: SeoPage[] = [
  guide('/snake-game-tips', 'Beginner tips', 'Snake Game Tips for Beginners: 15 Ways to Survive Longer | Snake Feast', 'Fifteen simple snake game tips that keep you alive longer and win more: where to move, when to eat, how to avoid traps, and the mistakes almost every new player makes.', ['classic', 'swipeControls'], 'Fifteen simple habits that keep a new snake alive and winning.'),
  guide('/advanced-snake-strategy', 'Advanced strategy', 'Advanced Snake Strategy: Cut-offs, Traps and Zone Control | Snake Feast', 'Advanced multiplayer snake strategy: cutting rivals off, trapping long snakes, playing the edges, using boosts and power-ups at the right moment, and staying calm in crowds.', ['kingOfTheHill', 'royaleMobile'], 'Cut-offs, traps, space control and timing for experienced players.'),
  guide('/snake-power-ups', 'Power-ups guide', 'Snake Power-ups Explained: All 9 Power-ups and When to Use Them | Snake Feast', 'Every Snake Feast power-up explained: ghost, double points, magnet, speed, armor, freeze, teleport, electric and fire, with how long they last and the best moment to grab each one.', ['magnet', 'bigArena'], 'What each of the nine power-ups does and when to grab it.'),
  guide('/snake-battle-royale', 'Battle royale', 'Snake Battle Royale Guide: How the Closing Zone Works and How to Win | Snake Feast', 'How snake battle royale works in Snake Feast: when the zone starts closing, how the warnings work, where to be, and strategies to be the last snake alive.', ['royaleZone', 'royaleMobile'], 'How the closing zone works and how to be the last snake alive.'),
  guide('/king-of-the-hill-snake', 'King of the Hill', 'King of the Hill Snake Mode: Rules, Scoring and Strategy | Snake Feast', 'King of the Hill in Snake Feast explained: how the hill scores, why you must hold it alone, how often it moves, and strategies to control it against three rivals.', ['kingOfTheHill'], 'Hold the hill alone to score, and keep everyone else off it.'),
  guide('/capture-the-food', 'Capture the Food', 'Capture the Food: Team Snake Mode Rules and Tactics | Snake Feast', 'Capture the Food is a team snake mode: steal the trophy from the middle, carry it to your base, and score three captures to win. Rules, roles and team tactics.', ['hero'], 'The team mode: steal the trophy, carry it home, win with three captures.'),
  guide('/big-arena-snake-game', 'Big arena', 'Big Arena: Play a Huge Live Snake Map with 24 Players | Snake Feast', 'The Snake Feast big arena is a huge live map for up to 24 snakes: drop in any time, respawn, choose lives and round length, fight the Titan boss and play the weekly events.', ['bigArena', 'magnet'], 'The huge live map: drop in, respawn, weekly events and the Titan boss.'),
  guide('/snake-game-controls', 'Controls guide', 'Snake Game Controls: Keyboard, Mouse, Swipe, Joystick and D-pad | Snake Feast', 'All the ways to control your snake in Snake Feast: arrow keys and WASD, mouse steering and click to boost on a computer, and swipe, joystick or D-pad on a phone, with settings that help.', ['swipeControls', 'classic'], 'Keyboard, mouse, swipe, joystick and D-pad, and the settings that help.'),
  guide('/daily-snake-challenge', 'Daily challenge', 'Daily Snake Challenge, Classic and Zen: Single-player Snake Modes | Snake Feast', 'The Snake Feast single-player modes: the daily challenge with one map for everyone, Classic with rising speed and rocks, and calm Zen with wrap-around edges. Tips for high scores.', ['classic'], 'One map for everyone each day, plus Classic and Zen for high scores.'),
  guide('/ranked-snake', 'Ranked play', 'Ranked Snake: Rating, Seasons, Tiers and Leaderboards | Snake Feast', 'How ranked play works in Snake Feast: duel, arena and team queues, your rating, monthly seasons from Bronze to Conqueror, the battle pass, leaderboards and fair-play rules.', ['royaleZone'], 'Rating, monthly seasons from Bronze to Conqueror, and leaderboards.'),
  guide('/snake-skins', 'Skins and customization', 'Snake Skins: Free Skins, Custom Snakes and How to Unlock More | Snake Feast', 'Every way to change how your snake looks in Snake Feast: ten free skins, the custom snake builder, missions, the daily login streak, the coin shop, the battle pass and season rewards.', ['hero', 'kingOfTheHill'], 'Free skins, the custom snake builder, and every way to unlock more.'),
  guide('/history-of-snake-game', 'History of Snake', 'The History of the Snake Game: From Arcades to Phones to Multiplayer | Snake Feast', 'A short history of the snake game: the 1970s arcade original, the home computer clones, the famous phone version, the rise of online .io snake games, and where multiplayer snake goes next.', ['classic'], 'From 1970s arcades to phones to today’s multiplayer snake.'),
];

/** The hub that lists every guide. */
export const GUIDES_HUB: SeoPage = {
  path: '/guides',
  name: 'Guides',
  title: 'Snake Feast Guides: Tips, Strategy, Modes and Power-ups',
  description: 'Every Snake Feast guide in one place: beginner tips, advanced strategy, all game modes, power-ups, controls, ranked play, skins and the history of the snake game.',
  images: ['bigArena'],
  priority: '0.8',
  changefreq: 'weekly',
};

export const SEO_PAGES: SeoPage[] = [
  {
    path: '/how-to-play',
    name: 'How to play',
    title: 'How to Play Snake Online — Rules, Controls, Power-ups & Tips | Snake Feast',
    description: 'Learn to play Snake Feast in a minute: controls for keyboard and phone, the rules, every game mode and power-up, the battle royale zone, and tips to win.',
    images: ['classic', 'swipeControls', 'magnet', 'royaleZone', 'kingOfTheHill', 'bigArena'],
    priority: '0.8',
    changefreq: 'monthly',
  },
  {
    path: '/multiplayer-snake-game',
    name: 'Play with friends',
    title: 'Multiplayer Snake Game — Play Snake with Friends Online, Free | Snake Feast',
    description: 'Play snake with friends online for free. Create a private room, share the code, and battle up to 8 players with voice chat, bots, teams and battle royale. No download.',
    images: ['hero', 'royaleMobile', 'bigArena'],
    priority: '0.8',
    changefreq: 'monthly',
  },
  GUIDES_HUB,
  ...GUIDE_PAGES,
  {
    path: '/about',
    name: 'About',
    title: 'About Snake Feast — A Free Multiplayer Snake Game Made by an Indie Developer',
    description: "Snake Feast is a free real-time multiplayer snake game built by an independent developer in India. Why it exists, how it works and what's coming next.",
    images: ['classic'],
    priority: '0.5',
    changefreq: 'monthly',
  },
  {
    path: '/contact',
    name: 'Contact & feedback',
    title: 'Contact & Feedback — Report a Bug or Share an Idea | Snake Feast',
    description: 'Report a bug, share an idea or get help with your account: send a message straight to the Snake Feast developer, or email support@snakefeast.com.',
    images: [],
    priority: '0.4',
    changefreq: 'yearly',
  },
  {
    path: '/delete-account',
    name: 'Delete account',
    title: 'Delete Your Snake Feast Account',
    description: 'How to permanently delete your Snake Feast account and data, in the app, on the web, or by email.',
    images: [],
    priority: '0.2',
    changefreq: 'yearly',
  },
  {
    path: '/privacy',
    name: 'Privacy policy',
    title: 'Privacy Policy — Snake Feast',
    description: 'How Snake Feast collects, uses and protects your data.',
    images: [],
    priority: '0.2',
    changefreq: 'yearly',
  },
  {
    path: '/terms',
    name: 'Terms of service',
    title: 'Terms of Service — Snake Feast',
    description: 'The rules for using Snake Feast, its accounts, virtual items and premium membership.',
    images: [],
    priority: '0.2',
    changefreq: 'yearly',
  },
];

/** Served by nginx as the body of every 404; never in the sitemap. */
export const NOT_FOUND_PAGE: SeoPage = {
  path: '/404',
  name: 'Page not found',
  title: 'Page not found — Snake Feast',
  description: 'This page does not exist.',
  images: [],
  priority: '0',
  changefreq: 'yearly',
  noindex: true,
};

/** The home page's own images, for the sitemap (its tags live in index.html). */
export const HOME_IMAGES: SeoImageId[] = ['hero', 'bigArena', 'royaleZone'];

export function seoPageFor(path: string): SeoPage | undefined {
  const clean = path.length > 1 ? path.replace(/\/+$/, '') : path;
  return SEO_PAGES.find((p) => p.path === clean);
}
